/**
 * Where the learner is standing, for surfaces that show a route.
 *
 * The roadmap on a career page used to be role-shaped: the same six stages for
 * a fourteen-year-old and for someone with ten years in the field, which meant
 * a Director role told a school student to "land the first role". This reads
 * the learner's own answers (qualification, years of work, age band) from their
 * completed run so the roadmap can be drawn from where they actually are.
 *
 * Returns null while loading or for a visitor with no run — callers then fall
 * back to the role-shaped roadmap, which is what an anonymous visitor gets.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { loadLatestCompletedSnapshot } from "@/features/assessment-v2/persistedSnapshot";
import type { LearnerStart } from "@/lib/careerRoadmap";
import { supabase } from "@/integrations/supabase/client";
import { recommendBand } from "@/lib/ageBand";

export function useLearnerStart(): LearnerStart | null {
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ["learner-start", user?.id ?? "anon"],
    enabled: Boolean(user?.id),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<LearnerStart | null> => {
      if (!user?.id) return null;
      const [state, profile] = await Promise.all([
        loadLatestCompletedSnapshot(),
        supabase.from("profiles").select("primary_goal, age, date_of_birth").eq("id", user.id).maybeSingle(),
      ]);
      if (!state) {
        // profiles.age is stored as text; resolve from date of birth first.
        const { age, band } = recommendBand(profile.data?.date_of_birth, profile.data?.age);
        return {
          primaryGoal: profile.data?.primary_goal ?? null,
          band: band ?? (age != null && age < 18 ? "under18" : null),
        };
      }
      // A teen or explorer has no work history to reason from, and we never ask
      // them for one. Treating a missing answer as zero years is correct here:
      // it keeps the roadmap starting at the beginning rather than assuming
      // experience nobody claimed.
      const teen = state.band !== "18plus";
      return {
        band: state.band,
        qualification: teen ? "school" : (state.part1?.qualification ?? null),
        yearsExperience: teen ? 0 : (state.part1?.years_experience ?? 0),
        primaryGoal: profile.data?.primary_goal ?? null,
        careerIntent: teen ? null : (state.part1?.career_intent ?? null),
      };
    },
  });

  return data ?? null;
}
